import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@hooks/useTheme";
import { RefreshIcon } from "../Icons";
import CustomSelect from "../UI/CustomSelect";
import { CustomDatePicker } from "../Forms";
import { createEmptyFormData } from "@utils/searchHelpers";

const PageHeaderWithSearch = ({
  title,
  searchFields,
  searchHandler,
  withoutSearch = false,
  gridCols = "xl:grid-cols-4", // Default grid layout
  padding = "p-6",
  fixedFields = {},
  "data-testid": dataTestId = "advanced-search",
  ...rest
}) => {
  const fieldTestId = (field) => `${dataTestId}-field-${field.name}`;
  const fieldContainerTestId = (field) => `${fieldTestId(field)}-container`;
  const fieldInputTestId = (field) => `${fieldTestId(field)}-input`;
  const { t } = useTranslation();
  const { colors } = useTheme();

  const controlClassName = `h-10 w-full rounded-lg border px-3 py-2 text-sm font-medium focus:outline-none ${colors.bgInput} ${colors.borderPrimary} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary} transition-colors duration-300`;

  const [isExpanded, setIsExpanded] = useState(false);
  const [formData, setFormData] = useState(() => createEmptyFormData(searchFields, fixedFields));

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    searchHandler(formData);
  };

  const handleReset = () => {
    const resetData = createEmptyFormData(searchFields, fixedFields);
    setFormData(resetData);
    searchHandler(resetData);
  };

  const toggleAccordion = () => {
    if (!withoutSearch) {
      setIsExpanded(!isExpanded);
    }
  };

  return (
    <div
      className={`${colors.bgPrimary} ${colors.borderPrimary} mb-5 rounded-lg border ${padding || "p-6"} shadow-lg`}
      data-testid={dataTestId}
      {...rest}
    >
      {/* Accordion Header */}
      <div
        data-testid={`${dataTestId}-toggle`}
        className={`flex items-center justify-between ${withoutSearch ? "cursor-default" : `cursor-pointer transition-colors duration-200 ${colors.hover}`
          } ${isExpanded ? `border-b ${colors.borderPrimary}` : ""} `}
        onClick={toggleAccordion}
        style={withoutSearch ? { pointerEvents: "none" } : {}}
      >
        <h3 className={`text-lg font-semibold ${colors.textPrimary}`} data-testid={`${dataTestId}-title`}>
          {title}
        </h3>
        {!withoutSearch && (
          <FontAwesomeIcon
            icon={faChevronDown}
            data-testid={`${dataTestId}-chevron`}
            className={`${colors.textMuted} transition-transform duration-200 ${isExpanded ? "rotate-180" : ""} `}
          />
        )}
      </div>

      {/* Accordion Body */}
      <div
        data-testid={`${dataTestId}-body`}
        className={`transition-all duration-300 ease-in-out ${isExpanded || withoutSearch ? "max-h-full opacity-100" : "max-h-0 opacity-0"} `}
      >
        <div className="pt-4" data-testid={`${dataTestId}-content`}>
          <form onSubmit={handleSearchSubmit} className="space-y-4" data-testid={`${dataTestId}-form`}>
            {/* Search Fields Grid */}
            <div className={`grid grid-cols-4 gap-4 min-w-[900px]`} data-testid={`${dataTestId}-fields-grid`}>
              {searchFields.map((field) => (
                <div key={field.name} className="flex flex-col" data-testid={fieldContainerTestId(field)}>
                  {field.type === "select" ? (
                    <CustomSelect
                      data-testid={fieldInputTestId(field)}
                      options={field.options || []}
                      value={field.options?.find((opt) => opt.value === formData[field.name]) || null}
                      onChange={(opt) => handleInputChange(field.name, opt ? opt.value : null)}
                      className="w-full text-sm font-medium"
                      placeholder={field.placeholder || ""}
                      displayFormatLabelWithID={field.displayFormatLabelWithID}
                    />
                  ) : field.type === "multiselect" ? (
                    <>
                      <CustomSelect
                        data-testid={fieldInputTestId(field)}
                        isMulti={true}
                        options={field.options || []}
                        value={formData[field.name] || null}
                        onChange={(opt) => handleInputChange(field.name, opt ? opt : [])}
                        className="w-full text-sm font-medium"
                        placeholder={field.placeholder || ""}
                        isSearchable={true}
                        clearable={true}
                        hideSelectedOptions={false}
                        closeMenuOnSelect={false}
                      />
                    </>
                  ) : field.type === "date" ? (
                    <>
                      <CustomDatePicker
                        data-testid={fieldInputTestId(field)}
                        selected={formData[field.name]}
                        onChange={(date) => handleInputChange(field.name, date)}
                        placeholderText={field.placeholder || "Select Date"}
                        className={`h-10 w-full appearance-none rounded-lg border p-3 text-sm font-medium focus:outline-none ${colors.bgInput} ${colors.borderPrimary} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary} `}
                        dateFormat="yyyy-MM-dd"
                        minDate={field.minDate ? formData[field.minDate] : undefined}
                      />
                    </>
                  ) : field.type === "dateFromTo" ? (
                    <>
                      <CustomDatePicker
                        data-testid={fieldInputTestId(field)}
                        selected={formData[field.name]?.startDate}
                        onChange={(dates) => {
                          const [start, end] = dates;
                          handleInputChange(field.name, { startDate: start, endDate: end });
                        }}
                        startDate={formData[field.name]?.startDate}
                        endDate={formData[field.name]?.endDate}
                        selectsRange
                        placeholderText={field.placeholder || "Select Date"}
                        className={`h-10 w-full appearance-none rounded-lg border p-3 text-sm font-medium focus:outline-none ${colors.bgInput} ${colors.borderPrimary} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary} `}
                        dateFormat="yyyy-MM-dd"
                      />
                    </>
                  ) : (
                    <>
                      <input
                        type={field.type || "text"}
                        id={field.name}
                        name={field.name}
                        data-testid={fieldInputTestId(field)}
                        value={formData[field.name] || ""}
                        onChange={(e) => handleInputChange(field.name, e.target.value)}
                        placeholder={field.placeholder}
                        className={controlClassName}
                      />
                    </>
                  )}
                </div>
              ))}
            </div>
            {/* Action Buttons */}
            <div className="flex items-center justify-end space-x-3" data-testid={`${dataTestId}-actions`}>
              <button
                type="button"
                data-testid={`${dataTestId}-reset-button`}
                onClick={handleReset}
                className={`inline-flex items-center px-4 py-2 text-sm font-semibold ${colors.textActive} rounded-md transition-colors ${colors.focusRing2} focus:outline-none`}
              >
                <RefreshIcon className="mr-2 h-4 w-4" data-testid={`${dataTestId}-reset-icon`} />
                {t("project.resetButton")}
              </button>
              <button
                type="submit"
                data-testid={`${dataTestId}-submit-button`}
                className={`px-4 py-2 text-sm font-semibold ${colors.buttonBackgroundGradient} rounded-md`}
              >
                {t("project.applyButton")}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PageHeaderWithSearch;
